import type { MeResponse, PermissionSet, UserPreferences } from "@starsach/shared-types";

/**
 * The information architecture (requirements.md section 8.9, 2026-09-18;
 * the order confirmed by the owner 2026-09-22): the modules everyone
 * gets - Customers second, because the fleet is where staff start - then
 * the organisation's own two, then Gairm's. Journeys, Flow configuration and Access are product areas
 * with sections of their own; the nav expands the active one. Every module
 * except Focus, Customers, Platform and Docs is about one environment,
 * carried in the path so a link says which.
 *
 * ADR-0017: a module or section names the permissions it `needs` (any one
 * of them); the rail shows it when some environment the person can see
 * grants one, and the page refuses an environment that does not.
 */
export type ModuleId = "focus" | "alarms" | "journeys" | "flow-configuration" | "quotas" | "trusted-advisor" | "support" | "access" | "audit" | "customers" | "platform";

export interface ModuleSection {
  id: string;
  label: string;
  /** Not built yet: the page says what it will be rather than 404ing. */
  planned?: boolean;
  /** Any of these on the environment shows the section; none named means the module's own. */
  needs?: readonly string[];
}

export interface Module {
  id: ModuleId;
  label: string;
  /** The path with no environment; `environmentScoped` modules append `/<environmentId>`. */
  path: string;
  environmentScoped: boolean;
  /** Also has a page with no environment - every customer at once - for staff (Trusted Advisor's list). */
  crossCustomer?: boolean;
  group: "operations" | "organization" | "gairm";
  sections?: ModuleSection[];
  /** Who sees it in the nav. Staff viewing a customer's portal (ADR-0011) count as the customer here. */
  staffOnly?: boolean;
  customerOnly?: boolean;
  /** Any of these on an environment shows the module (ADR-0017); none named means everyone with the role. */
  needs?: readonly string[];
}

export const JOURNEY_SECTIONS: ModuleSection[] = [
  { id: "overview", label: "Overview" },
  { id: "contacts", label: "Contacts", needs: ["journeys.contacts:view"] },
  { id: "paths", label: "Paths", needs: ["journeys.paths:view"] },
  { id: "concurrency", label: "Concurrency", needs: ["concurrency:enabled"] },
  { id: "rewind", label: "Rewind", planned: true },
  { id: "observability", label: "Observability", planned: true },
];

export const FLOW_CONFIGURATION_SECTIONS: ModuleSection[] = [
  { id: "profiles", label: "Profiles", needs: ["configuration.profiles:view"] },
  { id: "aliases", label: "Aliases", needs: ["configuration.bindings:view"] },
  { id: "promotions", label: "Promotions", needs: ["configuration.promotions:view"] },
  { id: "cache", label: "Connect cache" },
];

/** ADR-0017 section 6: People, Roles, Groups and the environment's chain and switches, each behind its own switch. */
export const ACCESS_SECTIONS: ModuleSection[] = [
  { id: "people", label: "People", needs: ["users:enabled"] },
  { id: "roles", label: "Roles", needs: ["roles:enabled"] },
  { id: "groups", label: "Groups", needs: ["roles:enabled"] },
  { id: "environments", label: "Environments", needs: ["environment:enabled"] },
];

export const MODULES: Module[] = [
  { id: "focus", label: "Focus", path: "/", environmentScoped: false, group: "operations" },
  { id: "customers", label: "Customers", path: "/customers", environmentScoped: false, group: "operations", staffOnly: true },
  { id: "journeys", label: "Journeys", path: "/journeys", environmentScoped: true, group: "operations", sections: JOURNEY_SECTIONS, needs: ["journeys:enabled"] },
  { id: "flow-configuration", label: "Flow configuration", path: "/flow-configuration", environmentScoped: true, group: "operations", sections: FLOW_CONFIGURATION_SECTIONS, needs: ["configuration:enabled"] },
  { id: "alarms", label: "Alarms", path: "/alarms", environmentScoped: true, group: "operations", needs: ["alarms:enabled"] },
  { id: "quotas", label: "Service quotas", path: "/quotas", environmentScoped: true, group: "operations", needs: ["quotas:enabled"] },
  { id: "trusted-advisor", label: "Trusted Advisor", path: "/trusted-advisor", environmentScoped: true, crossCustomer: true, group: "operations", needs: ["trusted_advisor:enabled"] },
  { id: "support", label: "Support cases", path: "/support", environmentScoped: true, group: "operations", needs: ["support_cases:enabled"] },
  { id: "access", label: "Access", path: "/access", environmentScoped: true, group: "organization", sections: ACCESS_SECTIONS, needs: ["users:enabled", "roles:enabled", "environment:enabled"] },
  { id: "audit", label: "Audit", path: "/audit", environmentScoped: true, group: "organization", needs: ["audit:enabled"] },
  { id: "platform", label: "Platform", path: "/platform", environmentScoped: false, group: "gairm", staffOnly: true },
];

/** A customer sees their environments under Organization; the same list page staff reach as Customers. */
export const ENVIRONMENTS_MODULE: Module = { id: "customers", label: "Environments", path: "/customers", environmentScoped: false, group: "organization", customerOnly: true };

export function moduleById(id: ModuleId): Module {
  return MODULES.find((m) => m.id === id)!;
}

/** The path a nav link takes: the module's, with the environment the person last looked at where the module needs one. */
export function modulePath(module: Module, environmentId: string | undefined, section?: string): string {
  if (!module.environmentScoped || !environmentId) return module.path;
  const base = `${module.path}/${encodeURIComponent(environmentId)}`;
  return section ? `${base}/${section}` : base;
}

/**
 * Section 13.1: the environment a module opens on from the rail - the
 * person's focus environment for it if they set one, else the one they
 * last looked at. A focus environment they can no longer see is ignored.
 */
export function focusEnvironment(module: Module, preferences: Pick<UserPreferences, "focusEnvironments">, visible: ReadonlySet<string> | undefined, last: string | undefined): string | undefined {
  const chosen = preferences.focusEnvironments[module.id];
  if (chosen && (!visible || visible.has(chosen))) return chosen;
  return last;
}

/** Whether the person is looking at the portal as a customer would - their own role, or staff inside a view-as tab. */
export function actsAsCustomer(me: MeResponse | undefined): boolean {
  return me?.role === "customer" || Boolean(me?.viewAs);
}

/** What the rail asks of the caller's access: any environment granting one of the permissions. Absent while access loads, which hides nothing. */
export type AccessAnywhere = ((permissions: readonly string[]) => boolean) | undefined;

export function visibleModules(me: MeResponse | undefined, canAnywhere?: AccessAnywhere): Module[] {
  const asCustomer = actsAsCustomer(me);
  const list = MODULES.filter((m) => (m.staffOnly ? !asCustomer : true)).filter((m) => !m.needs || !canAnywhere || canAnywhere(m.needs));
  return asCustomer ? [...list.filter((m) => m.group !== "gairm"), ENVIRONMENTS_MODULE] : list;
}

/** The sections of a module the set grants on this environment; a section with no `needs` follows the module. */
export function visibleSections(module: Module, set: PermissionSet | undefined): ModuleSection[] {
  return (module.sections ?? []).filter((s) => !s.needs || !set || set.hasAny(s.needs));
}

/** The old per-customer tab addresses, kept as redirects so a bookmark or a link in a case still lands. */
export const LEGACY_TABS: Record<string, { module: ModuleId; section?: string }> = {
  alarms: { module: "alarms" },
  quotas: { module: "quotas" },
  "trusted-advisor": { module: "trusted-advisor" },
  support: { module: "support" },
  concurrency: { module: "journeys", section: "concurrency" },
  journeys: { module: "journeys", section: "overview" },
  "flow-config": { module: "flow-configuration", section: "profiles" },
  access: { module: "access" },
  audit: { module: "audit" },
};
